import {chain, partition} from 'lodash';
import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {buildInboxSearchUrl} from '../../../../../../helpers/models/inboxHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {InboxModel} from '../../../../../../models/api/inboxModel';
import {ApiSearchInboxes} from '../../../../../../models/api/searchInboxModel';
import {fetchApiAsync} from '../../../../../../services/http/http';
import {BulkActionsTrackingTypesEnum} from '../../../../../../services/tracking';
import {AppState} from '../../../../../../store/appState';
import {
  getCanMoveToInaccessibleInbox,
  getCompanyUrl,
} from '../../../../../../store/data/company/companySelectors';
import {makeGetTrackers} from '../../../../../../store/data/conversations/conversationsSelectors';
import {
  bulkMoveToInboxAction,
  moveToInboxAction,
} from '../../../../../../store/data/conversations/moveConversationsActions';
import {updateDataAction} from '../../../../../../store/data/dataGroupsActions';
import {makeGetMoveTargetInboxes} from '../../../../../../store/data/inboxes/inboxesSelectors';
import {getUser} from '../../../../../../store/data/teammates/teammatesSelectors';
import {getTeamsByNamespace} from '../../../../../../store/data/teams/teamsSelectors';
import {getHasNewMoveToUnreachable} from '../../../../../../store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {DropdownFetcher} from '../../../../../common/dropdowns/source/asyncDropdownSource';
import {MoveDropdown, MoveHandler} from './moveDropdown';
import {filterInboxes} from './moveDropdownHelpers';

/*
 * Constants.
 */

const fetchInboxesPageSize = 50;

/*
 * Props.
 */

interface MoveDropdownContainerProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
}

/*
 * Selectors.
 */

function getConversations(_state: AppState, props: MoveDropdownContainerProps) {
  return props.conversations;
}

function makeGetConversationInboxIds() {
  return createSelector([getConversations], (conversations) => {
    const inboxIds = chain(conversations)
      .map((c) => c.inboxIds)
      .flatten()
      .value();

    return new Set(inboxIds);
  });
}

function makeGetPartitionedInboxes(
  getInboxes: ReturnType<typeof makeGetMoveTargetInboxes>,
  getConversationInboxIds: ReturnType<typeof makeGetConversationInboxIds>,
) {
  return createSelector([getInboxes, getConversationInboxIds], (inboxes, conversationInboxIds) =>
    partition(inboxes, (i) => conversationInboxIds.has(i.id)),
  );
}

function makeGetSortedInboxes(getPartitionedInboxes: ReturnType<typeof makeGetPartitionedInboxes>) {
  return createSelector([getPartitionedInboxes], ([conversationInboxes, otherInboxes]) => [
    ...conversationInboxes,
    ...otherInboxes,
  ]);
}

function makeGetConversationInboxes(getPartitionedInboxes: ReturnType<typeof makeGetPartitionedInboxes>) {
  return createSelector([getPartitionedInboxes], ([conversationInboxes]) => conversationInboxes);
}

interface PaginationState {
  nextPageIndex: number;
}

function makeGetFetchInaccessibleInboxesAsync(
  getConversationInboxIds: ReturnType<typeof makeGetConversationInboxIds>,
) {
  return createSelector(
    [getCompanyUrl, getConversationInboxIds],
    (companyUrl, conversationInboxIds): DropdownFetcher<InboxModel, PaginationState> =>
      async (query, inboxes, cancelToken, paginationData) => {
        // Build the URL and options.
        const nextPageIndex = paginationData?.nextPageIndex || 0;
        const url = buildInboxSearchUrl(companyUrl, query, {
          offset: nextPageIndex * fetchInboxesPageSize,
          limit: fetchInboxesPageSize,
          shouldIncludePrivate: true,
        });

        // Fetch the new inboxes.
        const fetchedInboxes = await fetchApiAsync(ApiSearchInboxes, url, {cancelToken});
        const canLoadMore = fetchedInboxes.inboxes.length === fetchedInboxes.limit;

        // Filter to non-conversation inboxes since conversation inboxes are already there if there's no query.
        const newInboxes = query
          ? fetchedInboxes.inboxes
          : fetchedInboxes.inboxes.filter((inbox) => !conversationInboxIds.has(inbox.id));
        const allInboxes = inboxes.concat(newInboxes);

        return {
          items: allInboxes,
          canLoadMore,
          paginationData: {
            nextPageIndex: nextPageIndex + 1,
          },
        };
      },
  );
}

function makeGetFetchAccessibleInboxesAsync(getAccessibleInboxes: ReturnType<typeof makeGetSortedInboxes>) {
  return createSelector(
    [getAccessibleInboxes],
    (accessibleInboxes): DropdownFetcher<InboxModel, PaginationState> =>
      async (query) => {
        // Currently, all the accessible inboxes are in the Redux store, so get them from there.
        // In the future, we may have to fetch this from the server like in getFetchInaccessibleInboxesAsync.
        const items = filterInboxes(accessibleInboxes, query);

        return {
          items,
          canLoadMore: false,
        };
      },
  );
}

function makeGetFetchInboxesAsync(
  getAccessibleInboxes: ReturnType<typeof makeGetSortedInboxes>,
  getConversationInboxIds: ReturnType<typeof makeGetConversationInboxIds>,
) {
  const getFetchAccessibleInboxesAsync = makeGetFetchAccessibleInboxesAsync(getAccessibleInboxes);
  const getFetchInaccessibleInboxesAsync = makeGetFetchInaccessibleInboxesAsync(getConversationInboxIds);
  return createSelector(
    [getCanMoveToInaccessibleInbox, getFetchInaccessibleInboxesAsync, getFetchAccessibleInboxesAsync],
    (canMoveToInaccessibleInbox, fetchInaccessibleInboxes, fetchAccessibleInboxes) =>
      canMoveToInaccessibleInbox ? fetchInaccessibleInboxes : fetchAccessibleInboxes,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MoveDropdownContainerProps>()(() => {
  const getConversationInboxIds = makeGetConversationInboxIds();
  const getInboxes = makeGetMoveTargetInboxes(getConversations);
  const getPartitionedInboxes = makeGetPartitionedInboxes(getInboxes, getConversationInboxIds);
  const getAccessibleInboxes = makeGetSortedInboxes(getPartitionedInboxes);
  const getConversationInboxes = makeGetConversationInboxes(getPartitionedInboxes);

  return {
    user: getUser,
    conversationInboxIds: getConversationInboxIds,
    conversationsInboxes: getConversationInboxes,
    teamsByNamespace: getTeamsByNamespace,
    fetchInboxesAsync: makeGetFetchInboxesAsync(getAccessibleInboxes, getConversationInboxIds),
    hasNewMoveToUnreachable: getHasNewMoveToUnreachable,
    trackers: makeGetTrackers(getConversations),
  };
});

export const MoveDropdownContainer = connect(
  mapStateToProps,
  {moveToInboxAction, updateDataAction, bulkMoveToInboxAction},
  (stateProps, dispatchProps, ownProps) => {
    const {inboxesParameters, cellsContext, conversations, onInterfaceEvent} = ownProps;
    const conversationIds = conversations.map((c) => c.id);

    const onMove: MoveHandler = (inbox, source, shouldRetainAccess) => {
      if (onInterfaceEvent) {
        onInterfaceEvent(BulkActionsTrackingTypesEnum.MOVE, false);
      }

      // TODO: Add inbox to the store after fetch and prevent it from being GC'ed.
      dispatchProps.updateDataAction({inboxes: [inbox]});
      dispatchProps.moveToInboxAction(
        cellsContext.teammateId,
        conversationIds,
        inbox.id,
        source,
        shouldRetainAccess,
      );
    };

    const onBulkMove = (inbox: InboxModel) => {
      if (onInterfaceEvent) {
        onInterfaceEvent(BulkActionsTrackingTypesEnum.MOVE, true);
      }

      dispatchProps.bulkMoveToInboxAction(inboxesParameters, cellsContext, inbox.id);
    };
    return {
      ...stateProps,
      ...ownProps,
      onMove,
      onBulkMove,
    };
  },
)(MoveDropdown);
